import { createContext, useContext, useReducer, ReactNode } from 'react'
import { BuilderAction, BuilderState } from './types'

// ── Initial State ────────────────────────────────────────────────────────────

const initialState: BuilderState = {
  step: 'setup',
  strategy: null,
  strategyId: null,
  catalog: [],
  selectedItems: [],
  aiRecommendations: [],
  aiSummary: '',
  isLoadingAi: false,
  isDeploying: false,
  deployTargets: [],
}

// ── Reducer ──────────────────────────────────────────────────────────────────

function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload }

    case 'SET_STRATEGY':
      return { ...state, strategy: action.payload }

    case 'SET_STRATEGY_ID':
      return { ...state, strategyId: action.payload }

    case 'SET_CATALOG':
      return { ...state, catalog: action.payload }

    case 'ADD_ITEM':
      return { ...state, selectedItems: [...state.selectedItems, action.payload] }

    case 'ADD_ITEMS':
      return { ...state, selectedItems: [...state.selectedItems, ...action.payload] }

    case 'REMOVE_ITEM':
      return {
        ...state,
        selectedItems: state.selectedItems.filter(i => i.itemId !== action.payload),
      }

    case 'UPDATE_ITEM':
      return {
        ...state,
        selectedItems: state.selectedItems.map(i =>
          i.itemId === action.payload.itemId ? { ...i, ...action.payload.updates } : i
        ),
      }

    case 'SET_SELECTED_ITEMS':
      return { ...state, selectedItems: action.payload }

    case 'SET_AI_RECOMMENDATIONS':
      return {
        ...state,
        aiRecommendations: action.payload.recommendations,
        aiSummary: action.payload.summary,
      }

    case 'SET_LOADING_AI':
      return { ...state, isLoadingAi: action.payload }

    case 'SET_DEPLOYING':
      return { ...state, isDeploying: action.payload }

    case 'SET_DEPLOY_TARGETS':
      return { ...state, deployTargets: action.payload }

    case 'RESET':
      return initialState

    default:
      return state
  }
}

// ── Context ──────────────────────────────────────────────────────────────────

interface BuilderContextValue {
  state: BuilderState
  dispatch: React.Dispatch<BuilderAction>
}

const BuilderContext = createContext<BuilderContextValue | null>(null)

export const useBuilderContext = () => {
  const ctx = useContext(BuilderContext)
  if (!ctx) throw new Error('useBuilderContext must be used within BuilderProvider')
  return ctx
}

export const BuilderProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(builderReducer, initialState)
  return <BuilderContext.Provider value={{ state, dispatch }}>{children}</BuilderContext.Provider>
}
